import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'

import { api } from '../lib/api'
import Layout from '../components/Layout'
import { Card, Spinner, ErrorMessage, Button, Field } from '../components/ui'
import { formatLabel } from '../lib/format'

function AuthPanel() {
  const queryClient = useQueryClient()
  const [mode, setMode] = useState('signIn')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')

  const submit = useMutation({
    mutationFn: () =>
      mode === 'signIn' ? api.signIn(email, password) : api.signUp(email, password),
    onSuccess: () => queryClient.invalidateQueries(),
  })

  return (
    <Card
      title={mode === 'signIn' ? 'Sign in' : 'Create an account'}
      subtitle="Your leagues stay tied to your account."
      className="mx-auto max-w-sm"
    >
      <form
        className="space-y-3"
        onSubmit={(event) => {
          event.preventDefault()
          submit.mutate()
        }}
      >
        <Field
          label="Email"
          type="email"
          autoComplete="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <Field
          label="Password"
          type="password"
          autoComplete={mode === 'signIn' ? 'current-password' : 'new-password'}
          required
          minLength={8}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />

        <ErrorMessage error={submit.error} />

        <Button type="submit" disabled={submit.isPending} className="w-full">
          {submit.isPending ? 'Working…' : mode === 'signIn' ? 'Sign in' : 'Sign up'}
        </Button>
      </form>

      <button
        type="button"
        className="mt-3 w-full text-xs underline text-slate-400"
        onClick={() => setMode(mode === 'signIn' ? 'signUp' : 'signIn')}
      >
        {mode === 'signIn' ? 'Need an account?' : 'Already have an account?'}
      </button>
    </Card>
  )
}

function LinkSleeper({ onLinked }) {
  const [username, setUsername] = useState('')

  const link = useMutation({
    mutationFn: () => api.linkSleeper(username),
    onSuccess: onLinked,
  })

  return (
    <Card
      title="Link your Sleeper account"
      subtitle="Sleeper's data is public and read-only, so no password is needed."
    >
      <form
        className="flex gap-2"
        onSubmit={(event) => {
          event.preventDefault()
          link.mutate()
        }}
      >
        <input
          className="flex-1 rounded-md px-3 py-2 text-sm border border-slate-600 bg-slate-900"
          placeholder="Sleeper username"
          value={username}
          required
          onChange={(e) => setUsername(e.target.value)}
        />
        <Button type="submit" disabled={link.isPending}>
          {link.isPending ? 'Looking up…' : 'Link'}
        </Button>
      </form>

      <div className="mt-2">
        <ErrorMessage error={link.error} />
      </div>
    </Card>
  )
}

function LeaguePicker({ account }) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const available = useQuery({
    queryKey: ['availableLeagues', account.id],
    queryFn: () => api.availableLeagues(account.id),
  })

  const connect = useMutation({
    mutationFn: (externalId) => api.connectLeague(account.id, externalId),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['leagues'] })
      navigate(`/leagues/${data.league.id}/trade`)
    },
  })

  if (available.isPending) return <Spinner label="Loading your leagues…" />
  if (available.error) return <ErrorMessage error={available.error} />

  const leagues = available.data?.available_leagues || []

  if (leagues.length === 0) {
    return (
      <p className="text-sm text-slate-400">
        No leagues found for {account.username} this season.
      </p>
    )
  }

  return (
    <div className="space-y-2">
      <ErrorMessage error={connect.error} />

      {leagues.map((league) => (
        <div
          key={league.external_id}
          className="flex items-center justify-between rounded-md px-3 py-2 border border-slate-700"
        >
          <div>
            <div className="text-sm font-medium text-slate-100">
              {league.name}
            </div>
            <div className="text-xs text-slate-400">
              {league.season} · {league.num_teams} teams
            </div>
          </div>

          <Button
            variant={league.connected ? 'secondary' : 'primary'}
            disabled={connect.isPending}
            onClick={() => connect.mutate(league.external_id)}
          >
            {connect.isPending && connect.variables === league.external_id
              ? 'Syncing…'
              : league.connected
                ? 'Re-sync'
                : 'Import'}
          </Button>
        </div>
      ))}
    </div>
  )
}

export default function Connect() {
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  const session = useQuery({
    queryKey: ['session'],
    queryFn: () => api.session(),
    retry: false,
  })

  const accounts = useQuery({
    queryKey: ['platformAccounts'],
    queryFn: () => api.platformAccounts(),
    enabled: !!session.data,
  })

  const leagues = useQuery({
    queryKey: ['leagues'],
    queryFn: () => api.leagues(),
    enabled: !!session.data,
  })

  if (session.isPending) {
    return (
      <Layout>
        <Spinner />
      </Layout>
    )
  }

  if (session.error) {
    return (
      <Layout>
        <AuthPanel />
      </Layout>
    )
  }

  const account = accounts.data?.platform_accounts?.[0]
  const connected = leagues.data?.leagues || []

  return (
    <Layout>
      <div className="space-y-4">
        {connected.length > 0 && (
          <Card title="Your leagues">
            <div className="space-y-2">
              {connected.map((league) => (
                <button
                  key={league.id}
                  type="button"
                  onClick={() => navigate(`/leagues/${league.id}/trade`)}
                  className="flex w-full items-center justify-between rounded-md px-3 py-2 text-left border border-slate-700 hover:border-slate-500"
                >
                  <div>
                    <div className="text-sm font-medium text-slate-100">
                      {league.name}
                    </div>
                    <div className="text-xs text-slate-400">
                      {league.season} · {league.num_teams} teams ·{' '}
                      {formatLabel(league.format?.ppr_type)}
                      {league.format?.superflex ? ' · Superflex' : ''}
                    </div>
                  </div>
                  <span className="text-xs text-slate-400">Build a trade →</span>
                </button>
              ))}
            </div>
          </Card>
        )}

        {account ? (
          <Card
            title="Import a league"
            subtitle={`Linked to Sleeper as ${account.username}`}
          >
            <LeaguePicker account={account} />
          </Card>
        ) : (
          <LinkSleeper
            onLinked={() => {
              queryClient.invalidateQueries({ queryKey: ['platformAccounts'] })
            }}
          />
        )}
      </div>
    </Layout>
  )
}
